import moment from 'moment-timezone'
import { BlackoutDate } from './types'
import { rrulestr, RRule, Options } from 'rrule'
import { HoursOfOperation } from 'shared-libs/generated/server-types/entity/locationEntity'

export const parseTimeToMinutes = (timeString: string): number => {
  const [hours, minutes] = timeString.split(':').map(Number)
  return hours * 60 + (minutes || 0)
}

/**
 * Checks if a time slot overlaps with any blackout dates.
 */
const isBlackedOut = (
  resourceId: string | undefined,
  blackoutDates: BlackoutDate[],
  startDate: Date,
  endDate: Date
): boolean => {
  const startMoment = moment(startDate)
  const endMoment = moment(endDate)

  for (const blackout of blackoutDates) {
    // if (blackout.resourceIds?.length > 0) {
    //   if (!resourceId || !blackout.resourceIds?.includes(resourceId)) {
    //     continue
    //   }
    // }

    const blackoutStartMoment = moment(blackout.timeWindow.start!.dateTime)
    const blackoutEndMoment = moment(blackout.timeWindow.end!.dateTime)

    if (blackout.rrule) {
      const rule = rrulestr(blackout.rrule, { dtstart: blackoutStartMoment.toDate() })
      const occurrences = rule.between(
        startMoment.clone().subtract(1, 'year').toDate(),
        endMoment.clone().add(1, 'year').toDate(),
        true
      )

      for (const occurrence of occurrences) {
        const duration = blackoutEndMoment.diff(blackoutStartMoment)
        const occurrenceStart = moment(occurrence)
        const occurrenceEnd = occurrenceStart.clone().add(duration, 'milliseconds')

        if (intervalsIntersect(occurrenceStart, occurrenceEnd, startMoment, endMoment)) {
          return true
        }
      }
    } else {
      if (intervalsIntersect(blackoutStartMoment, blackoutEndMoment, startMoment, endMoment)) {
        return true
      }
    }
  }

  return false
}

export type TimeSlotAvailability = {
  available: boolean
  reason?: 'blackout' | 'hours'
}

/**
 * Checks availability with detailed reason for unavailability.
 */
export const checkTimeSlotAvailability = (
  resourceId: string | undefined,
  hoursOfOperation: { [resourceId: string]: HoursOfOperation },
  blackoutDates: BlackoutDate[] = [],
  ...dates: Date[]
): TimeSlotAvailability => {
  if (dates.length === 0) {
    return { available: true }
  }

  const startDate = dates[0]
  const endDate = dates.length > 1 ? dates[dates.length - 1] : dates[0]

  if (isBlackedOut(resourceId, blackoutDates, startDate, endDate)) {
    return { available: false, reason: 'blackout' }
  }

  if (!resourceId || !hoursOfOperation[resourceId]) {
    return { available: true }
  }

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  const resourceHours = hoursOfOperation[resourceId]

  const dateInfos = dates.map((date) => ({
    day: dayNames[date.getDay()],
    timeInMinutes: date.getHours() * 60 + date.getMinutes(),
  }))

  for (const schedule of resourceHours) {
    if (schedule.isOpen24Hours) {
      if (dateInfos.every((info) => schedule.daysOfWeek?.includes(info.day as any))) {
        return { available: true }
      }
    }

    if (schedule.hours) {
      for (const interval of schedule.hours) {
        if (interval.start?.dateTime && interval.end?.dateTime) {
          const startTime = parseTimeToMinutes(interval.start.dateTime)
          const endTime = parseTimeToMinutes(interval.end.dateTime)

          if (
            dateInfos.every((info, index) => {
              const isValidDay = schedule.daysOfWeek?.includes(info.day as any)
              const isAfterStart = info.timeInMinutes >= startTime
              const isLastDate = index === dateInfos.length - 1
              const isTimeRange = dateInfos.length > 1
              const isBeforeEnd =
                isLastDate && isTimeRange
                  ? info.timeInMinutes <= endTime
                  : info.timeInMinutes < endTime

              return isValidDay && isAfterStart && isBeforeEnd
            })
          ) {
            return { available: true }
          }
        }
      }
    }
  }

  return { available: false, reason: 'hours' }
}

// TODO: is this purely local (only hours of operation), require dodging
// existing appointments for a given resource, or should this hit the server?
/**
 * Checks availability for either a moment or time interval, given a resource
 * and its hours of operation.
 */
export const isTimeSlotAvailable = (
  resourceId: string | undefined,
  hoursOfOperation: { [resourceId: string]: HoursOfOperation },
  blackoutDates: BlackoutDate[] = [],
  ...dates: Date[]
): boolean => {
  return checkTimeSlotAvailability(resourceId, hoursOfOperation, blackoutDates, ...dates).available
}

function intervalsIntersect(
  start1: moment.Moment,
  end1: moment.Moment,
  start2: moment.Moment,
  end2: moment.Moment
) {
  return start1.isBefore(end2) && start2.isBefore(end1)
}

export function generateRRuleString(
  recurrenceType: 'daily' | 'weekly' | 'monthly',
  recurrenceInterval: number,
  recurrenceEndType: 'never' | 'count' | 'until',
  recurrenceCount: number,
  recurrenceUntil: string,
  startDateTime: string
): string {
  let freq: number

  switch (recurrenceType) {
    case 'daily':
      freq = RRule.DAILY
      break
    case 'weekly':
      freq = RRule.WEEKLY
      break
    case 'monthly':
      freq = RRule.MONTHLY
      break
    default:
      freq = RRule.WEEKLY
  }

  const options: Partial<Options> = {
    freq,
    interval: recurrenceInterval,
    dtstart: new Date(startDateTime),
  }

  if (recurrenceEndType === 'count') {
    options.count = recurrenceCount
  } else if (recurrenceEndType === 'until') {
    options.until = new Date(recurrenceUntil)
  }

  const rule = new RRule(options)
  return rule.toString()
}
